import { Box, Heading, useToast, Icon, HStack } from '@chakra-ui/react';
import { RiAddLine } from 'react-icons/ri';
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import debounce from 'lodash.debounce';
import { DefaultLayout } from '../../../_layout/DefaultLayout';
import { LinkButton } from '../../../../../components/LinkButton';
import {
  maskCpf,
  maskShortDate,
} from '../../../../../utils/formatters/handleMask';
import { IMember, MemberTable } from './components/MemberTable';
import { listUsersService } from '../../../../../services/Users/ListUsersService';
import { translateError } from '../../../../../utils/errors';
import { useAuth } from '../../../../../hooks/auth';

interface ILoadMembersProps {
  cpf?: string;
  email?: string;
  experience?: string;
  isBlockedOrSuspended?: boolean;
  name?: string;
  phone?: string;
  ventureId?: string;
  sort?: 'cpf' | 'name' | 'phone' | 'email' | 'createdAt';
  order?: 'ASC' | 'DESC';
  page?: number;
}

export const MemberList = (): JSX.Element => {
  const toast = useToast();

  const { assignment: authenticatedAssignment, venture: authenticatedVenture } =
    useAuth();

  const [membersList, setMembersList] = useState<IMember[]>([]);
  const [currentFilters, setCurrentFilters] =
    useState<{ [key: string]: string }>();
  const [currentOrdering, setCurrentOrdering] = useState<'ASC' | 'DESC'>(
    'DESC',
  );
  const [isBlockedOrSuspended, setIsBlockedOrSuspended] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterBy, setFilterBy] = useState('name');
  const [sortBy, setSortBy] = useState<
    'cpf' | 'name' | 'phone' | 'email' | 'createdAt'
  >('createdAt');
  const [totalPages, setTotalPages] = useState<number>();
  const [totalItems, setTotalItems] = useState<number>();
  const [showDeleted, setShowDeleted] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const canReadDeleted =
    !!authenticatedAssignment?.featureGroup.key.includes('MANAGER');

  const loadMembers = useCallback(
    async ({
      cpf,
      email,
      experience,
      isBlockedOrSuspended: blockedOrSuspendedFilter,
      name,
      phone,
      page,
      sort,
      order,
    }: ILoadMembersProps) => {
      try {
        const members = await listUsersService({
          cpf: cpf || undefined,
          email: email || undefined,
          experience: Number(experience) >= 0 ? Number(experience) : undefined,
          name: name || undefined,
          phone: phone || undefined,
          featureGroups: ['MEMBER', 'DEPENDANT'],
          isBlockedOrSuspended: blockedOrSuspendedFilter,
          assignmentStatus: showDeleted ? 'DELETED' : undefined,
          limit: 20,
          page,
          sort,
          order,
        });

        const parsedMembers = members.items.map((member) => ({
          ...member,
          cpf: member.cpf ? maskCpf(member.cpf) : undefined,
          formattedDate: maskShortDate(member.createdAt),
        }));

        setTotalPages(members.pages);
        setTotalItems(members.total);
        setMembersList(parsedMembers);
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          toast({
            title: 'Falha ao carregar dados',
            description:
              translateError({ message: err.response?.data.message }) ||
              'Ocorreu um erro ao carregar os dados dos membros, tente novamente.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      }
    },
    [toast, showDeleted],
  );

  useEffect(() => {
    loadMembers({
      ...currentFilters,
      order: currentOrdering,
      isBlockedOrSuspended,
      page: currentPage,
      sort: sortBy,
    });
  }, [
    currentFilters,
    currentPage,
    isBlockedOrSuspended,
    sortBy,
    loadMembers,
    currentOrdering,
    authenticatedVenture,
    reloadKey,
  ]);

  const handleFilterBy = useCallback((value: string) => {
    setFilterBy(value);
  }, []);

  const handleFilter = useCallback(
    (value: string) => {
      setCurrentFilters({ [filterBy]: value });
    },
    [filterBy],
  );

  const debounceHandleFilter = debounce(handleFilter, 1000);

  const handleSortList = useCallback(
    (sort: 'cpf' | 'name' | 'phone' | 'email' | 'createdAt') => {
      setSortBy(sort);
    },
    [],
  );

  const handleOrderList = useCallback((order: 'ASC' | 'DESC') => {
    setCurrentOrdering(order);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleToggleBlockedOrSuspended = useCallback((value: boolean) => {
    setCurrentPage(1);
    setIsBlockedOrSuspended(value);
  }, []);

  const handleToggleShowDeleted = useCallback(() => {
    setShowDeleted((prevState) => !prevState);
    setCurrentPage(1);
  }, []);

  const handleReload = useCallback(() => {
    setReloadKey((prevState) => prevState + 1);
  }, []);

  return (
    <DefaultLayout>
      <Box flex="1" borderRadius={8} bg="white" p="8">
        <HStack justify="space-between">
          <Heading size="lg" fontWeight="normal">
            Membros
          </Heading>

          {authenticatedAssignment?.featureGroup.features.some((feature) =>
            ['USER_FULL_ACCESS', 'USER_WRITE_ACCESS'].includes(feature.key),
          ) && (
            <LinkButton
              colorScheme="green"
              icon={<Icon as={RiAddLine} fontSize="16" />}
              to="/members/register"
            >
              Cadastrar novo
            </LinkButton>
          )}
        </HStack>

        <MemberTable
          mt="4"
          members={membersList}
          showDeleted={showDeleted}
          onToggleShowDeleted={
            canReadDeleted ? handleToggleShowDeleted : undefined
          }
          onAssignmentChanged={handleReload}
          currentPage={currentPage}
          filterBy={filterBy}
          onChangeFilterBy={handleFilterBy}
          onFilter={debounceHandleFilter}
          onToggleBlockedOrSuspended={handleToggleBlockedOrSuspended}
          onOrder={handleOrderList}
          onPageChange={handlePageChange}
          onSort={handleSortList}
          order={currentOrdering}
          isBlockedOrSuspended={isBlockedOrSuspended}
          sortBy={sortBy}
          totalPages={totalPages}
          totalItems={totalItems}
        />
      </Box>
    </DefaultLayout>
  );
};
